"use client";

import { createContext, ReactNode, useContext, useMemo } from "react";

// How wide a component is actually drawn, per breakpoint. Nothing on the page knows
// this from the DOM at render time — a media request has to be built before the
// browser has laid anything out — so each block passes the width it hands its cells
// down to whatever renders inside them.
export type WidthBudget = {
    xs: number;
    sm2: number;
    lg: number;
};

// The landing container caps itself at 1400; a full-width block spans the viewport.
export const CONTAINER_WIDTH: WidthBudget = { xs: 400, sm2: 900, lg: 1400 };
export const FULLWIDTH_WIDTH: WidthBudget = { xs: 420, sm2: 1000, lg: 1920 };

// A component rendered outside any block sees the widest case, which is what it
// would have requested anyway.
const WidthBudgetContext = createContext<WidthBudget>(FULLWIDTH_WIDTH);

export const useWidthBudget = () => useContext(WidthBudgetContext);

export function WidthBudgetProvider({ value, children }: { value: WidthBudget; children: ReactNode }) {
    // The object is rebuilt on every block render; memoising on the numbers keeps the
    // context value stable so nested components do not re-render for nothing.
    const budget = useMemo(() => value, [value.xs, value.sm2, value.lg]); // eslint-disable-line react-hooks/exhaustive-deps

    return <WidthBudgetContext.Provider value={budget}>{children}</WidthBudgetContext.Provider>;
}

const mapBudget = (budget: WidthBudget, pick: (value: number, key: keyof WidthBudget) => number): WidthBudget => ({
    xs: pick(budget.xs, 'xs'),
    sm2: pick(budget.sm2, 'sm2'),
    lg: pick(budget.lg, 'lg'),
});

// Never promise less than this: a slot narrower than a thumbnail is either a
// measurement mistake or a decoration, and asking for a tiny file makes it blurry.
const MIN_BUDGET = 120;

export function capWidthBudget(budget: WidthBudget, cap: WidthBudget): WidthBudget {
    return mapBudget(budget, (value, key) => Math.min(value, cap[key]));
}

// One grid cell of a 12-column block, minus its own horizontal padding. The column
// gap is left out on purpose: `sizes` is an upper bound, and over-stating it by a
// few pixels is cheaper than a cell that picks the source one bucket too small.
export function narrowWidthBudget(
    budget: WidthBudget,
    columns: WidthBudget,
    paddingX: WidthBudget = { xs: 0, sm2: 0, lg: 0 },
): WidthBudget {
    return mapBudget(budget, (value, key) => {
        const col = columns[key];
        if (!col) return MIN_BUDGET;

        return Math.max(Math.round((value * col) / 12) - 2 * paddingX[key], MIN_BUDGET);
    });
}

// The pixel width to ask the media server for, from the CSS width a slot is drawn at.
// Doubled for retina, capped so a full-bleed background never pulls a 4K file, and
// floored so a small decoration still survives a zoom. The server clamps anything
// wider than the original, so an over-estimate costs nothing.
export function mediaWidthFor(cssWidth: number) {
    return Math.min(Math.max(Math.round(cssWidth * 2), 200), 1920);
}
